import React from 'react';
import { Retry } from '@components/common';
import { DefaultLayer as Loading } from '@components/common/Retry/Elements';
import DrillDownTreemap from '../DrillDownTreemap';
import { useDurationRoot, useDimensionGroupBy } from '../hooks/useTreemapHooks';
import { DURATION_BUCKET, DURATION_BUCKET_COLOR, arddDimension, arddMetric } from '../constants';
import { WidgetContainer } from '../../styled';
import { useInteraction, useConflictFilters, type Level } from '../DrillDownTreemap/hooks';
import { useExternalState } from '../hooks/useExternalState';
import { getFilters } from '../helpers';
import NetworkContext from '../context';
import type { NavItem } from '../DrillDownTreemap/Breadcrumb';
import { useAvailable } from '../hooks/useAvailable';

const rootKey = 'OVERALL';

const drillDownLevels: Level = {
  key: rootKey, // all distribution
  next: {
    key: 'network_request_url_host', // 1-2s
    next: {
      key: 'network_request_url_path', // 1-2s, host
    },
  },
};

const rootNavItem: NavItem = {
  label: 'Network Request Count',
  key: rootKey,
  filter: undefined,
};

function createLevel(level: Level) {
  if (level.key === rootKey) {
    return {
      type: 'root',
      labelField: arddDimension,
      valueField: arddMetric,
      drillDownField: '',
      partitionBy: arddDimension,
      next: level.next,
    } as const;
  }
  return {
    type: 'children',
    labelField: 'name',
    valueField: 'AverageRequestCount',
    drillDownField: level.key,
    next: level.next,
    partitionBy: (item: Record<string, any>) => {
      const duration = item.AverageRequestDuration;
      const idx = DURATION_BUCKET.findIndex((i) => duration < i);
      if (idx !== -1) {
        const prev = DURATION_BUCKET[idx - 1];
        return prev
          ? `${DURATION_BUCKET[idx - 1]}-${DURATION_BUCKET[idx]}`
          : `0-${DURATION_BUCKET[idx]}`;
      }
      return '>=10';
    },
  } as const;
}

function TreeMapDuration(props?: { withTotals?: boolean }) {
  const { withTotals = false } = props ?? {};
  const { enableSSF, enableTimeSlice, onSelect, onResetCallback, formatter } =
    React.useContext(NetworkContext);
  const { interval } = useAvailable(enableSSF, enableTimeSlice);
  const { enableLoading, keepPreviousData, reset } = useExternalState(enableSSF);

  const { navItems, levelInfo, onNavigate, onDrillDown, onReset } = useInteraction({
    rootNavItem,
    drillDownLevels,
    createLevel,
    disabledDrillDownKeys: ['event_name'],
    onSelect,
    onResetCallback,
  });

  // reset to root when real time mode is on
  React.useEffect(() => {
    if (reset) {
      onReset();
    }
  }, [onReset, reset]);

  const {
    data: root,
    total: rootTotal,
    ...rootStatus
  } = useDurationRoot({
    interval,
    enabled: levelInfo.type === 'root',
    keepPreviousData,
    enableSSF,
    withTotals,
  });

  const allFilters = getFilters(navItems);

  const hasConflict = useConflictFilters(allFilters.filters, onReset);

  const {
    data: children,
    total: childrenTotal,
    ...childrenStatus
  } = useDimensionGroupBy({
    dimension: levelInfo.drillDownField,
    metrics: ['AverageRequestDuration', 'AverageRequestCount'],
    sortByMetric: 'AverageRequestCount',
    interval,
    keepPreviousData,
    enabled:
      !hasConflict &&
      levelInfo.type === 'children' &&
      levelInfo.drillDownField === navItems[navItems.length - 1].key,
    enableSSF,
    withTotals,
    ...allFilters,
  });

  const status = levelInfo.type === 'root' ? rootStatus : childrenStatus;

  const data = React.useMemo(() => {
    if (levelInfo.type === 'root') {
      return root?.filter((item) => item[arddMetric]);
    }
    return children;
  }, [root, children, levelInfo.type]);

  return (
    <WidgetContainer $isEmpty={status.isEmpty}>
      <Retry {...status} isEmpty={false} progress>
        {enableLoading && status.isLoading && <Loading progress duration={1} />}
        <DrillDownTreemap
          id="duration"
          title="Network Request Count"
          formatter={formatter}
          colorMap={DURATION_BUCKET_COLOR}
          navItems={navItems}
          total={withTotals ? (levelInfo.type === 'root' ? rootTotal : childrenTotal) : undefined}
          data={data}
          drillDown={!!levelInfo.next}
          labelField={levelInfo.labelField}
          valueField={levelInfo.valueField}
          partitionBy={levelInfo.partitionBy}
          onDrillDown={onDrillDown}
          onNavigate={onNavigate}
        />
      </Retry>
    </WidgetContainer>
  );
}

export default TreeMapDuration;
